import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  getDocs,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

// ========================================
// LISTS
// ========================================

export const createList = async (listData) => {
  try {
    const docRef = await addDoc(collection(db, 'lists'), {
      ...listData,
      createdAt: Timestamp.now()
    });
    return { id: docRef.id, ...listData };
  } catch (error) {
    console.error('Create list error:', error);
    throw error;
  }
};

export const updateList = async (listId, updates) => {
  try {
    await updateDoc(doc(db, 'lists', listId), updates);
  } catch (error) {
    console.error('Update list error:', error);
    throw error;
  }
};

export const deleteList = async (listId) => {
  try {
    await deleteDoc(doc(db, 'lists', listId));
  } catch (error) {
    console.error('Delete list error:', error);
    throw error;
  }
};

export const getUserLists = (userId, callback) => {
  const q = query(
    collection(db, 'lists'),
    where('members', 'array-contains', userId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const lists = [];
    snapshot.forEach((doc) => {
      lists.push({ id: doc.id, ...doc.data() });
    });
    callback(lists);
  });
};

// ========================================
// CHORES
// ========================================

export const createChore = async (choreData) => {
  try {
    const docRef = await addDoc(collection(db, 'chores'), {
      ...choreData,
      createdAt: Timestamp.now()
    });
    return { id: docRef.id, ...choreData };
  } catch (error) {
    console.error('Create chore error:', error);
    throw error;
  }
};

export const updateChore = async (choreId, updates) => {
  try {
    await updateDoc(doc(db, 'chores', choreId), updates);
  } catch (error) {
    console.error('Update chore error:', error);
    throw error;
  }
};

export const deleteChore = async (choreId) => {
  try {
    await deleteDoc(doc(db, 'chores', choreId));
  } catch (error) {
    console.error('Delete chore error:', error);
    throw error;
  }
};

export const getListChores = (listId, callback) => {
  const q = query(
    collection(db, 'chores'),
    where('listId', '==', listId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const chores = [];
    snapshot.forEach((doc) => {
      chores.push({ id: doc.id, ...doc.data() });
    });
    callback(chores);
  });
};

// ========================================
// CATEGORIES
// ========================================

export const createCategory = async (categoryData) => {
  try {
    const docRef = await addDoc(collection(db, 'categories'), {
      ...categoryData,
      createdAt: Timestamp.now()
    });
    return { id: docRef.id, ...categoryData };
  } catch (error) {
    console.error('Create category error:', error);
    throw error;
  }
};

export const updateCategory = async (categoryId, updates) => {
  try {
    await updateDoc(doc(db, 'categories', categoryId), updates);
  } catch (error) {
    console.error('Update category error:', error);
    throw error;
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    await deleteDoc(doc(db, 'categories', categoryId));
  } catch (error) {
    console.error('Delete category error:', error);
    throw error;
  }
};

export const getListCategories = (listId, callback) => {
  const q = query(
    collection(db, 'categories'),
    where('listId', '==', listId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const categories = [];
    snapshot.forEach((doc) => {
      categories.push({ id: doc.id, ...doc.data() });
    });
    callback(categories);
  });
};

// ========================================
// COMPLETIONS
// ========================================

export const createCompletion = async (completionData) => {
  try {
    const docRef = await addDoc(collection(db, 'completions'), {
      ...completionData,
      date: Timestamp.now()
    });
    return { id: docRef.id, ...completionData };
  } catch (error) {
    console.error('Create completion error:', error);
    throw error;
  }
};

export const getListCompletions = (listId, callback) => {
  const q = query(
    collection(db, 'completions'),
    where('listId', '==', listId),
    orderBy('date', 'desc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const completions = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      completions.push({ 
        id: doc.id, 
        ...data,
        // Convert Firestore Timestamp to ISO string for compatibility
        date: data.date?.toDate?.()?.toISOString() || data.date
      });
    });
    callback(completions);
  });
};

// ========================================
// BULK OPERATIONS
// ========================================

export const deleteListData = async (listId) => {
  try {
    // Delete all chores
    const choresQuery = query(collection(db, 'chores'), where('listId', '==', listId));
    const choresSnapshot = await getDocs(choresQuery);
    choresSnapshot.forEach(async (doc) => {
      await deleteDoc(doc.ref);
    });

    // Delete all categories
    const categoriesQuery = query(collection(db, 'categories'), where('listId', '==', listId));
    const categoriesSnapshot = await getDocs(categoriesQuery);
    categoriesSnapshot.forEach(async (doc) => {
      await deleteDoc(doc.ref);
    });

    // Delete all completions
    const completionsQuery = query(collection(db, 'completions'), where('listId', '==', listId));
    const completionsSnapshot = await getDocs(completionsQuery);
    completionsSnapshot.forEach(async (doc) => {
      await deleteDoc(doc.ref);
    });

    // Delete the list itself
    await deleteList(listId);
  } catch (error) {
    console.error('Delete list data error:', error);
    throw error;
  }
};
